import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Megaphone,
  Facebook,
  Instagram,
  Plus,
  MessageCircle,
  TrendingUp,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';
import { socialCaptureService, type SocialPage } from '@/services/social-capture.service';
import { channelsService, type MetaPage } from '@/services/channels.service';
import { Modal } from '@/components/common/Modal';

export const SocialCapturePage: React.FC = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState<SocialPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showPageSelector, setShowPageSelector] = useState(false);
  const [availablePages, setAvailablePages] = useState<MetaPage[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string>('');

  useEffect(() => {
    socialCaptureService
      .getPages()
      .then(setPages)
      .catch(() => toast.error('Failed to load connected pages'))
      .finally(() => setLoading(false));
  }, []);

  const handleToggleActive = async (e: React.MouseEvent, page: SocialPage) => {
    e.stopPropagation();
    try {
      const updated = await socialCaptureService.updatePage(page.id, {
        is_active: !page.is_active,
      });
      setPages((prev) => prev.map((p) => (p.id === page.id ? updated : p)));
      toast.success(updated.is_active ? 'Page activated' : 'Page paused');
    } catch {
      toast.error('Failed to update page status');
    }
  };

  const platformIcon = (platform: string) => {
    if (platform === 'instagram') return Instagram;
    return Facebook;
  };

  const platformColor = (platform: string) => {
    if (platform === 'instagram')
      return 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400';
    return 'bg-blue-600';
  };

  const loadPages = () => {
    socialCaptureService
      .getPages()
      .then(setPages)
      .catch(() => toast.error('Failed to load connected pages'));
  };

  const handleConnectPage = async () => {
    setConnecting(true);
    try {
      const redirectUri = `${window.location.origin}/channels/meta/callback`;
      const { url } = await channelsService.getMetaOAuthUrl(redirectUri);

      const width = 600, height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(url, 'meta_oauth', `width=${width},height=${height},left=${left},top=${top}`);

      const handleMessage = async (event: MessageEvent) => {
        if (event.data?.type === 'META_OAUTH_CALLBACK' && event.data?.code) {
          window.removeEventListener('message', handleMessage);
          popup?.close();

          try {
            const result = await channelsService.handleMetaCallback(event.data.code, redirectUri);
            setAvailablePages(result.pages);
            setShowPageSelector(true);
          } catch (err: any) {
            toast.error(err.message || 'Failed to process Meta authorization');
          } finally {
            setConnecting(false);
          }
        }
        if (event.data?.type === 'META_OAUTH_ERROR') {
          window.removeEventListener('message', handleMessage);
          popup?.close();
          toast.error('Facebook authorization was denied or failed');
          setConnecting(false);
        }
      };
      window.addEventListener('message', handleMessage);

      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          setConnecting(false);
        }
      }, 1000);
    } catch (err: any) {
      toast.error(err.message || 'Failed to start Meta authorization');
      setConnecting(false);
    }
  };

  const handleSelectPage = async (page: MetaPage) => {
    setSelectedPageId(page.id);
    try {
      // Connect Facebook page (backend resolves token from Meta OAuth cache)
      await socialCaptureService.connectPage({
        platform: 'facebook',
        platform_page_id: page.id,
        page_name: page.name,
        credentials: { from_meta_cache: true },
      });

      // If Instagram account is linked, connect that too
      if (page.instagram_account) {
        await socialCaptureService.connectPage({
          platform: 'instagram',
          platform_page_id: page.instagram_account.id,
          page_name: page.name + ' (Instagram)',
          page_username: page.instagram_account.username || undefined,
          credentials: { from_meta_cache: true, facebook_page_id: page.id },
        });
        toast.success(`Connected ${page.name} (Facebook + Instagram)`);
      } else {
        toast.success(`Connected ${page.name}`);
      }

      setShowPageSelector(false);
      setAvailablePages([]);
      setSelectedPageId('');
      loadPages();
    } catch (err: any) {
      toast.error(err.message || 'Failed to connect page');
      setSelectedPageId('');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Megaphone className="w-6 h-6 text-pink-600" />
            <h1 className="text-2xl font-bold text-[#1a1a2e]">Social Capture</h1>
          </div>
          <p className="text-sm text-gray-500">
            Turn social media comments into conversations and revenue.
          </p>
        </div>
        <button
          onClick={handleConnectPage}
          disabled={connecting}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#1a1a2e] rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0px_#1a1a2e] transition-all disabled:opacity-50"
        >
          {connecting ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          {connecting ? 'Connecting...' : 'Connect Page'}
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="w-6 h-6 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && pages.length === 0 && (
        <div className="text-center py-24 border-2 border-dashed border-gray-200 rounded-xl">
          <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">
            No pages connected yet
          </h3>
          <p className="text-sm text-gray-400 mb-4">
            Connect a Facebook or Instagram page to start capturing leads from comments.
          </p>
          <button
            onClick={handleConnectPage}
            disabled={connecting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#1a1a2e] rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0px_#1a1a2e] transition-all disabled:opacity-50"
          >
            {connecting ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {connecting ? 'Connecting...' : 'Connect Page'}
          </button>
        </div>
      )}

      {/* Page cards grid */}
      {!loading && pages.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages.map((page) => {
            const Icon = platformIcon(page.platform);
            const dmsSent = page.stats?.total_dms ?? 0;
            const conversions = page.stats?.total_conversions ?? 0;

            return (
              <div
                key={page.id}
                onClick={() => navigate(`/social-capture/${page.id}`)}
                className="group cursor-pointer bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0px_#1a1a2e] transition-all p-4"
              >
                {/* Top row: icon + name + toggle */}
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0',
                      platformColor(page.platform)
                    )}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-[#1a1a2e] truncate">
                      {page.page_name}
                    </h3>
                    {page.page_username && (
                      <p className="text-xs text-gray-400 truncate">
                        @{page.page_username}
                      </p>
                    )}
                  </div>
                  {/* Active/Paused toggle */}
                  <button
                    onClick={(e) => handleToggleActive(e, page)}
                    className={cn(
                      'relative w-9 h-5 rounded-full transition-colors flex-shrink-0',
                      page.is_active ? 'bg-emerald-500' : 'bg-gray-300'
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform',
                        page.is_active ? 'translate-x-4' : 'translate-x-0.5'
                      )}
                    />
                  </button>
                </div>

                {/* Stats row */}
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-3.5 h-3.5" />
                    {dmsSent} DMs sent
                  </span>
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3.5 h-3.5" />
                    {conversions} conversions
                  </span>
                </div>

                {/* Bottom: keywords count + arrow */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-400">
                    {page.keyword_count ?? 0} keyword{(page.keyword_count ?? 0) !== 1 ? 's' : ''}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Page selector modal */}
      {showPageSelector && (
        <Modal isOpen onClose={() => { setShowPageSelector(false); setAvailablePages([]); }} title="Select a Page" size="md">
          <div className="space-y-2">
            {availablePages.map(page => (
              <button
                key={page.id}
                onClick={() => handleSelectPage(page)}
                disabled={selectedPageId === page.id}
                className="w-full text-left p-3 rounded-lg border-2 border-gray-200 hover:border-violet-500 hover:bg-violet-50 transition-all disabled:opacity-50"
              >
                <p className="font-semibold text-sm">{page.name}</p>
                {page.instagram_account && (
                  <p className="text-xs text-gray-500">Instagram: @{page.instagram_account.username}</p>
                )}
              </button>
            ))}
            {availablePages.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No pages found. Make sure your Facebook account manages at least one page.</p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};
